import {
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material'
import { US_STATES } from '@tnf-recruit/shared'
import { formatUsPhone } from '../../../lib/phone'
import type { AthleteProfileFormApi, ProfileDataState } from '../../../hooks/useAthleteProfileForm'

/** Personal info + parent/guardian fields, shared by the onboarding wizard and the edit form. */
export const PersonalInfoFields = ({ form }: { form: AthleteProfileFormApi }) => {
  const { data, fieldErrors, setData, validatePersonalField } = form

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="First Name"
          value={data.personalInfo.firstName}
          error={!!fieldErrors['personalInfo.firstName']}
          helperText={fieldErrors['personalInfo.firstName']}
          onChange={e => {
            const next = { ...data.personalInfo, firstName: e.target.value }
            setData({ ...data, personalInfo: next })
            validatePersonalField('firstName', 'personalInfo.firstName', next)
          }}
          onBlur={() =>
            validatePersonalField('firstName', 'personalInfo.firstName', data.personalInfo)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Last Name"
          value={data.personalInfo.lastName}
          error={!!fieldErrors['personalInfo.lastName']}
          helperText={fieldErrors['personalInfo.lastName']}
          onChange={e => {
            const next = { ...data.personalInfo, lastName: e.target.value }
            setData({ ...data, personalInfo: next })
            validatePersonalField('lastName', 'personalInfo.lastName', next)
          }}
          onBlur={() =>
            validatePersonalField('lastName', 'personalInfo.lastName', data.personalInfo)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Email Address"
          type="email"
          value={data.personalInfo.email}
          error={!!fieldErrors['personalInfo.email']}
          helperText={fieldErrors['personalInfo.email']}
          onChange={e => {
            const next = { ...data.personalInfo, email: e.target.value }
            setData({ ...data, personalInfo: next })
            validatePersonalField('email', 'personalInfo.email', next)
          }}
          onBlur={() => validatePersonalField('email', 'personalInfo.email', data.personalInfo)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Mobile Phone Number"
          value={data.personalInfo.mobilePhone}
          error={!!fieldErrors['personalInfo.mobilePhone']}
          helperText={fieldErrors['personalInfo.mobilePhone']}
          onChange={e => {
            const next = { ...data.personalInfo, mobilePhone: formatUsPhone(e.target.value) }
            setData({ ...data, personalInfo: next })
            validatePersonalField('mobilePhone', 'personalInfo.mobilePhone', next)
          }}
          onBlur={() =>
            validatePersonalField('mobilePhone', 'personalInfo.mobilePhone', data.personalInfo)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Date of Birth"
          type="date"
          value={data.personalInfo.dateOfBirth}
          error={!!fieldErrors['personalInfo.dateOfBirth']}
          helperText={fieldErrors['personalInfo.dateOfBirth']}
          onChange={e => {
            const next = { ...data.personalInfo, dateOfBirth: e.target.value }
            setData({ ...data, personalInfo: next })
            validatePersonalField('dateOfBirth', 'personalInfo.dateOfBirth', next)
          }}
          onBlur={() =>
            validatePersonalField('dateOfBirth', 'personalInfo.dateOfBirth', data.personalInfo)
          }
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required error={!!fieldErrors['personalInfo.genderIdentity']}>
          <InputLabel>Gender Identity</InputLabel>
          <Select
            value={data.personalInfo.genderIdentity}
            label="Gender Identity"
            onChange={e => {
              const value = e.target.value
              const next: ProfileDataState['personalInfo'] = {
                ...data.personalInfo,
                genderIdentity: value,
              }
              // Auto-set competition team when selecting a binary identity
              if (value === 'male') {
                next.competitionTeam = 'men'
              } else if (value === 'female') {
                next.competitionTeam = 'women'
              } else {
                next.competitionTeam = ''
              }
              setData({ ...data, personalInfo: next })
              validatePersonalField('genderIdentity', 'personalInfo.genderIdentity', next)
            }}
            onBlur={() =>
              validatePersonalField(
                'genderIdentity',
                'personalInfo.genderIdentity',
                data.personalInfo
              )
            }
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="non-binary">Non-binary</MenuItem>
            <MenuItem value="other">Other</MenuItem>
            <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
          </Select>
          {fieldErrors['personalInfo.genderIdentity'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
              {fieldErrors['personalInfo.genderIdentity']}
            </Typography>
          )}
        </FormControl>
      </Grid>
      {(data.personalInfo.genderIdentity === 'non-binary' ||
        data.personalInfo.genderIdentity === 'other' ||
        data.personalInfo.genderIdentity === 'prefer-not-to-say') && (
        <>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={!!fieldErrors['personalInfo.competitionTeam']}>
              <InputLabel>Competition Team</InputLabel>
              <Select
                value={data.personalInfo.competitionTeam}
                label="Competition Team"
                onChange={e => {
                  const next = {
                    ...data.personalInfo,
                    competitionTeam: e.target.value as 'men' | 'women',
                  }
                  setData({ ...data, personalInfo: next })
                  validatePersonalField('competitionTeam', 'personalInfo.competitionTeam', next)
                }}
                onBlur={() =>
                  validatePersonalField(
                    'competitionTeam',
                    'personalInfo.competitionTeam',
                    data.personalInfo
                  )
                }
              >
                <MenuItem value="men">Men&apos;s Team</MenuItem>
                <MenuItem value="women">Women&apos;s Team</MenuItem>
              </Select>
              {fieldErrors['personalInfo.competitionTeam'] && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                  {fieldErrors['personalInfo.competitionTeam']}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              This determines which team (men&apos;s or women&apos;s) you will appear under when
              recruiters search for athletes for specific roster needs.
            </Typography>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Address Line 1"
          value={data.personalInfo.address.line1}
          error={!!fieldErrors['personalInfo.address.line1']}
          helperText={fieldErrors['personalInfo.address.line1']}
          onChange={e => {
            const nextAddr = { ...data.personalInfo.address, line1: e.target.value }
            const next = { ...data.personalInfo, address: nextAddr }
            setData({ ...data, personalInfo: next })
            validatePersonalField('address.line1', 'personalInfo.address.line1', next)
          }}
          onBlur={() =>
            validatePersonalField('address.line1', 'personalInfo.address.line1', data.personalInfo)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address Line 2"
          value={data.personalInfo.address.line2}
          onChange={e =>
            setData({
              ...data,
              personalInfo: {
                ...data.personalInfo,
                address: { ...data.personalInfo.address, line2: e.target.value },
              },
            })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="City"
          value={data.personalInfo.address.city}
          error={!!fieldErrors['personalInfo.address.city']}
          helperText={fieldErrors['personalInfo.address.city']}
          onChange={e => {
            const nextAddr = { ...data.personalInfo.address, city: e.target.value }
            const next = { ...data.personalInfo, address: nextAddr }
            setData({ ...data, personalInfo: next })
            validatePersonalField('address.city', 'personalInfo.address.city', next)
          }}
          onBlur={() =>
            validatePersonalField('address.city', 'personalInfo.address.city', data.personalInfo)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required error={!!fieldErrors['personalInfo.address.state']}>
          <InputLabel>State</InputLabel>
          <Select
            value={data.personalInfo.address.state}
            onChange={e => {
              const nextAddr = { ...data.personalInfo.address, state: e.target.value }
              const next = { ...data.personalInfo, address: nextAddr }
              setData({ ...data, personalInfo: next })
              validatePersonalField('address.state', 'personalInfo.address.state', next)
            }}
            onBlur={() =>
              validatePersonalField(
                'address.state',
                'personalInfo.address.state',
                data.personalInfo
              )
            }
          >
            {US_STATES.map(state => (
              <MenuItem key={state.code} value={state.code}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors['personalInfo.address.state'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
              {fieldErrors['personalInfo.address.state']}
            </Typography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Country/Region"
          value={data.personalInfo.country}
          error={!!fieldErrors['personalInfo.country']}
          helperText={fieldErrors['personalInfo.country']}
          onChange={e => {
            const next = { ...data.personalInfo, country: e.target.value }
            setData({ ...data, personalInfo: next })
            validatePersonalField('country', 'personalInfo.country', next)
          }}
          onBlur={() => validatePersonalField('country', 'personalInfo.country', data.personalInfo)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="ZIP Code"
          value={data.personalInfo.address.zipCode}
          error={!!fieldErrors['personalInfo.address.zipCode']}
          helperText={fieldErrors['personalInfo.address.zipCode']}
          onChange={e => {
            const nextAddr = { ...data.personalInfo.address, zipCode: e.target.value }
            const next = { ...data.personalInfo, address: nextAddr }
            setData({ ...data, personalInfo: next })
            validatePersonalField('address.zipCode', 'personalInfo.address.zipCode', next)
          }}
          onBlur={() =>
            validatePersonalField(
              'address.zipCode',
              'personalInfo.address.zipCode',
              data.personalInfo
            )
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Bio (Optional)"
          value={data.personalInfo.bio}
          onChange={e =>
            setData({
              ...data,
              personalInfo: { ...data.personalInfo, bio: e.target.value },
            })
          }
        />
      </Grid>

      {/* Parent/Guardian Section */}
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Parent / Guardian Contact
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Please provide contact information for a parent or guardian.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Parent/Guardian First Name"
          value={data.personalInfo.parentGuardian.firstName}
          error={!!fieldErrors['personalInfo.parentGuardian.firstName']}
          helperText={fieldErrors['personalInfo.parentGuardian.firstName']}
          onChange={e => {
            const nextPg = { ...data.personalInfo.parentGuardian, firstName: e.target.value }
            const next = { ...data.personalInfo, parentGuardian: nextPg }
            setData({ ...data, personalInfo: next })
            validatePersonalField(
              'parentGuardian.firstName',
              'personalInfo.parentGuardian.firstName',
              next
            )
          }}
          onBlur={() =>
            validatePersonalField(
              'parentGuardian.firstName',
              'personalInfo.parentGuardian.firstName',
              data.personalInfo
            )
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Parent/Guardian Last Name"
          value={data.personalInfo.parentGuardian.lastName}
          error={!!fieldErrors['personalInfo.parentGuardian.lastName']}
          helperText={fieldErrors['personalInfo.parentGuardian.lastName']}
          onChange={e => {
            const nextPg = { ...data.personalInfo.parentGuardian, lastName: e.target.value }
            const next = { ...data.personalInfo, parentGuardian: nextPg }
            setData({ ...data, personalInfo: next })
            validatePersonalField(
              'parentGuardian.lastName',
              'personalInfo.parentGuardian.lastName',
              next
            )
          }}
          onBlur={() =>
            validatePersonalField(
              'parentGuardian.lastName',
              'personalInfo.parentGuardian.lastName',
              data.personalInfo
            )
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Parent/Guardian Email"
          type="email"
          value={data.personalInfo.parentGuardian.email}
          error={!!fieldErrors['personalInfo.parentGuardian.email']}
          helperText={fieldErrors['personalInfo.parentGuardian.email']}
          onChange={e => {
            const nextPg = { ...data.personalInfo.parentGuardian, email: e.target.value }
            const next = { ...data.personalInfo, parentGuardian: nextPg }
            setData({ ...data, personalInfo: next })
            validatePersonalField('parentGuardian.email', 'personalInfo.parentGuardian.email', next)
          }}
          onBlur={() =>
            validatePersonalField(
              'parentGuardian.email',
              'personalInfo.parentGuardian.email',
              data.personalInfo
            )
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Parent/Guardian Phone Number"
          value={data.personalInfo.parentGuardian.phoneNumber}
          error={!!fieldErrors['personalInfo.parentGuardian.phoneNumber']}
          helperText={fieldErrors['personalInfo.parentGuardian.phoneNumber']}
          onChange={e => {
            const nextPg = {
              ...data.personalInfo.parentGuardian,
              phoneNumber: formatUsPhone(e.target.value),
            }
            const next = { ...data.personalInfo, parentGuardian: nextPg }
            setData({ ...data, personalInfo: next })
            validatePersonalField(
              'parentGuardian.phoneNumber',
              'personalInfo.parentGuardian.phoneNumber',
              next
            )
          }}
          onBlur={() =>
            validatePersonalField(
              'parentGuardian.phoneNumber',
              'personalInfo.parentGuardian.phoneNumber',
              data.personalInfo
            )
          }
        />
      </Grid>
    </Grid>
  )
}
